// Model Bench's own three short sounds, made in the browser (no audio files): played when a training run or a
// model export or compile finishes, so someone can look away while it runs.
//
// They follow Material Design's sound principles: brief, soft, simple, and never an alarm. A cue is a handful of notes
// with a gentle start, little echo, and nothing past about a second and a quarter.
// The voice is still ours: a glassy bell (a sine carrier with a fast, inharmonic FM overtone that dies away quickly)
// and, once, a soft wooden tap at the start of "trained", the bench being knocked on.
//   trained  a soft tap on E4, then bells climbing E5 B5 E6, the last one ringing out.
//   ready    two bells, G#5 then C#6: a short "there you go".
//   problem  two low, dull bells going down, quiet: something needs a look, nothing is wrong with you.
// All in E major pentatonic, so the three belong together.

export type Cue = "trained" | "ready" | "problem";

/** `soft` scales the bell's bright FM strike: 1 is the glassy default, near 0 is a plain rounded tone. */
export interface Note { at: number; freq: number; voice: "tap" | "bell"; gain: number; ring: number; soft?: number }

export const CUES: Record<Cue, Note[]> = {
  trained: [
    { at: 0.00, freq: 329.63, voice: "tap", gain: 0.34, ring: 0.22 },
    { at: 0.10, freq: 659.25, voice: "bell", gain: 0.30, ring: 0.45 },
    { at: 0.22, freq: 987.77, voice: "bell", gain: 0.28, ring: 0.50 },
    { at: 0.34, freq: 1318.51, voice: "bell", gain: 0.30, ring: 0.90 },
  ],
  ready: [
    { at: 0.00, freq: 830.61, voice: "bell", gain: 0.30, ring: 0.35 },
    { at: 0.12, freq: 1108.73, voice: "bell", gain: 0.32, ring: 0.55 },
  ],
  problem: [
    { at: 0.00, freq: 246.94, voice: "bell", gain: 0.26, ring: 0.35, soft: 0.15 },
    { at: 0.16, freq: 207.65, voice: "bell", gain: 0.24, ring: 0.50, soft: 0.15 },
  ],
};

/** How long a cue lasts, ring included, in seconds. */
export const cueLength = (cue: Cue): number => Math.max(...CUES[cue].map((n) => n.at + n.ring));

function tap(ctx: BaseAudioContext, out: AudioNode, t: number, n: Note): void {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(n.freq * 1.04, t);
  osc.frequency.exponentialRampToValueAtTime(n.freq, t + 0.03);        // the knock: pitch settles as the mallet leaves
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(n.gain, t + 0.004);
  env.gain.exponentialRampToValueAtTime(0.0001, t + n.ring);
  osc.connect(env).connect(out);
  osc.start(t); osc.stop(t + n.ring + 0.05);

  // The click: a few milliseconds of band-passed noise on the front of the note.
  const len = Math.max(1, Math.floor(ctx.sampleRate * 0.02));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.sin(i * 12.9898) * 43758.5453 % 1) * (1 - i / len);   // fixed noise: the same every time
  const src = ctx.createBufferSource();
  const band = ctx.createBiquadFilter();
  const click = ctx.createGain();
  band.type = "bandpass"; band.frequency.value = Math.min(n.freq * 6, 5000); band.Q.value = 1.2;
  click.gain.value = n.gain * 0.25;
  src.buffer = buf;
  src.connect(band).connect(click).connect(out);
  src.start(t);
}

function bell(ctx: BaseAudioContext, out: AudioNode, t: number, n: Note): void {
  const carrier = ctx.createOscillator();
  const mod = ctx.createOscillator();
  const depth = ctx.createGain();
  const env = ctx.createGain();
  carrier.type = "sine"; carrier.frequency.value = n.freq;
  mod.type = "sine"; mod.frequency.value = n.freq * 3.51;               // not a whole multiple: the glassy part
  const strike = n.freq * 1.2 * (n.soft ?? 1);
  depth.gain.setValueAtTime(Math.max(strike, 0.01), t);
  depth.gain.exponentialRampToValueAtTime(Math.max(strike * 0.02, 0.005), t + Math.min(0.3, n.ring * 0.4));   // bright at the strike, pure after
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(n.gain, t + 0.006);
  env.gain.exponentialRampToValueAtTime(0.0001, t + n.ring);
  mod.connect(depth).connect(carrier.frequency);
  carrier.connect(env).connect(out);
  carrier.start(t); mod.start(t);
  carrier.stop(t + n.ring + 0.05); mod.stop(t + n.ring + 0.05);
}

/** Draw *cue* into *ctx* starting at *start* seconds; returns when it ends. Works on a live or an offline context. */
export function renderCue(ctx: BaseAudioContext, cue: Cue, start = 0, dest: AudioNode = ctx.destination): number {
  const master = ctx.createGain();
  master.gain.value = 0.5;
  // A touch of room: one soft echo, darkened, so the bell does not stop dead.
  const echo = ctx.createDelay(1.0);
  const feedback = ctx.createGain();
  const tone = ctx.createBiquadFilter();
  const wet = ctx.createGain();
  echo.delayTime.value = 0.12; feedback.gain.value = 0.2; wet.gain.value = 0.12;
  tone.type = "lowpass"; tone.frequency.value = 3200;
  master.connect(dest);
  master.connect(echo); echo.connect(tone); tone.connect(feedback); feedback.connect(echo); tone.connect(wet); wet.connect(dest);
  for (const n of CUES[cue]) (n.voice === "tap" ? tap : bell)(ctx, master, start + n.at, n);
  return start + cueLength(cue) + 0.6;
}

// ── On or off ─────────────────────────────────────────────────────────────────────────────────────────────────────
const KEY = "modelbench.sound";
export function soundOn(): boolean {
  try { return globalThis.localStorage.getItem(KEY) !== "off"; } catch { return true; }
}
export function setSoundOn(on: boolean): void {
  try { globalThis.localStorage.setItem(KEY, on ? "on" : "off"); } catch { /* the choice lasts for this visit only */ }
}

// ── Playing ───────────────────────────────────────────────────────────────────────────────────────────────────────
let shared: AudioContext | null = null;
function context(): AudioContext | null {
  if (shared) return shared;
  const g = globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (!Ctor) return null;
  try { shared = new Ctor(); } catch { return null; }
  return shared;
}

/** Browsers keep sound off until the person has touched the page; the first click or key opens it for later cues. */
let unlockArmed = false;
export function unlockAudio(): void {
  if (unlockArmed || typeof document === "undefined") return;
  unlockArmed = true;
  const open = () => {
    void context()?.resume().catch(() => {});
    document.removeEventListener("pointerdown", open, true);
    document.removeEventListener("keydown", open, true);
  };
  document.addEventListener("pointerdown", open, true);
  document.addEventListener("keydown", open, true);
}

/** Play *cue* unless sounds are off or the browser cannot. Never throws: a missing sound must not break the page. */
export function playCue(cue: Cue, force = false): void {
  if (!force && !soundOn()) return;
  try {
    const ctx = context();
    if (!ctx) return;
    const go = () => renderCue(ctx, cue, ctx.currentTime + 0.03);
    if (ctx.state === "suspended") void ctx.resume().then(go).catch(() => {});
    else go();
  } catch { /* nothing to play with */ }
}
